import React, { useState, useEffect, Fragment, useCallback } from "react";
import './../trip-create.style.scss';
import { isValid, isValidArray, getArrayfromString } from "../../../utils/utilities";
import { useDispatch, useSelector } from 'react-redux';
import { Form, TimePicker, Row, Button, Input, Select, message, Col, InputNumber, Divider, Switch, Checkbox, Tooltip } from 'antd';
import moment from 'moment';
import { EditBlockedSeats } from "../edit-block-seats";
import { scheduleSelector } from "../../../selector/trip-create.selector";
import { userPartnerSelectors } from "../../../selector/user.selector";

const { Option } = Select;

const isFareAvailable = (values, busSelected) => {
    let fareAvailable = false;
    if (isValid(values['fare'])) {
        fareAvailable = true;
    } else {
        if (isValidArray(busSelected)) {
            let seatGroupIds = getArrayfromString(busSelected.seat_group_ids, ',');
            seatGroupIds.forEach(element => {
                if (isValid(values[element])) {
                    fareAvailable = true;
                }
            });
        }
    }
    return fareAvailable;
}

const renderPossibleFares = (busSelected) => {
    if (isValidArray(busSelected)) {
        let fareComponents = [];
        let isSeatGroupIdsExits = isValidArray(busSelected) && isValid(busSelected.seat_group_ids) ? true : false;
        if (busSelected.supported_fare) {
            fareComponents.push(
                <Col>
                    <Form.Item
                        name="fare"
                        tooltip="without gst"
                        rules={[{ required: true, message: '' }]}
                    >
                        <InputNumber
                            placeholder="Fare"
                            style={{ width: '150px' }}
                        />
                    </Form.Item>
                </Col>);
        }
        if (isSeatGroupIdsExits) {
            let seatGroupIds = getArrayfromString(busSelected.seat_group_ids, ',');
            seatGroupIds.forEach(element => {
                fareComponents.push(<Col>
                    <Form.Item
                        name={element}
                        tooltip="without gst"
                        rules={[{ required: true, message: '' }]}
                    >
                        <InputNumber
                            placeholder={element}
                            disabled={isValid(busSelected) ? false : true}
                            style={{ width: '150px' }}
                        />
                    </Form.Item>
                </Col>);
            });
        }
        return (
            <Row gutter={[16, 16]}>
                {fareComponents}
            </Row>
        );
    } else {
        return null;
    }
}

export const BusSelectionForm = (props) => {
    const {
        busesData,
        selectedSeatNumbers,
        setSelectedSeatNumbers,
        onMethodSelect,
        busFormData,
        setSelectedPartner,
        tripScheduleId
    } = props;

    const [busSelected, setBusSelected] = useState(null);
    const [formeDetails, updateForm] = useState({});
    const [busesOptions, setBusesOptions] = useState([]);
    const [isSeatSharing, setSeatSharing] = useState(false);
    const [form] = Form.useForm();

    const dispatch = useDispatch();
    const partnerDetails = useSelector(userPartnerSelectors);
    const schedule = useSelector(scheduleSelector);

    const timeFormat = 'HH:mm';

    // Initialize form with existing busFormData if available
    useEffect(() => {
        if (busFormData) {
            // Find the selected bus from busesData
            const selectedBus = busesData.find(bus => bus.id === busFormData.bus_id);
            if (selectedBus) {
                setBusSelected(selectedBus);
            }
            
            // Ensure start_time is a proper moment object
            let processedBusFormData = { ...busFormData };
            if (busFormData.start_time) {
                if (!moment.isMoment(busFormData.start_time)) {
                    // If it's not a moment object, try to convert it
                    if (typeof busFormData.start_time === 'string') {
                        processedBusFormData.start_time = moment(busFormData.start_time, timeFormat);
                    } else {
                        // Fallback to current time
                        processedBusFormData.start_time = moment();
                    }
                }
                // Ensure it's a valid moment object
                if (!processedBusFormData.start_time.isValid()) {
                    processedBusFormData.start_time = moment();
                }
            }
            
            // Set form data and values
            updateForm(processedBusFormData);
            form.setFieldsValue(processedBusFormData);
            setSeatSharing(busFormData.cut_route_enabled ? true : false);
            
            // Set partner if available
            if (busFormData.partner_code && partnerDetails) {
                const partner = partnerDetails.find(p => p.partner_code === busFormData.partner_code);
                if (partner) {
                    setSelectedPartner(partner);
                }
            }
        } else if (isValidArray(schedule)) {
            let intialFareJson = {};
            if (isValid(schedule.fareJson)) {
                intialFareJson = JSON.parse(schedule.fareJson);
            }
            let startTime = null
            if (isValid(schedule.startTime)) {
                startTime = moment(schedule.startTime, timeFormat);
            }
            let formObject = {
                start_time: startTime,
                bus_id: schedule.busId,
                trip_identifier: schedule.name,
                fare: schedule.fare,
                cut_route_enabled: schedule.cutRouteEnabled,
                bp_check_enabled: schedule.bpCheckEnabled,
                ...intialFareJson
            };
            updateForm(formObject);
            form.setFieldsValue(formObject);
            setSeatSharing(schedule.cutRouteEnabled ? true : false);
            let seatNos = getArrayfromString(schedule.blockedSeatNumbers);
            setSelectedSeatNumbers(seatNos);
            setBusSelected(schedule.busSelected);
        } else {
            form.resetFields();
            updateForm({});
        }
    }, [busFormData, schedule, busesData, form, partnerDetails]);

    useEffect(()=>{
        let tempBusesOptions = [];
        busesData.map((bus)=>{
            tempBusesOptions.push({value: bus.id, label: bus.description, data: bus});
        })
        setBusesOptions(tempBusesOptions);
    },[busesData]);

    const printPartnerOption = (option) => {
        return <Option value={option.partner_code} data={option} key={option.partner_code}>{option.partner_name}</Option>
    };

    const updateStartTime = (time) => {
        form.setFieldsValue({
            start_time: time
        });
    }

    const handleMethodSelect = useCallback((method) => {
        
        form.validateFields()
            .then((values) => {
                formeDetails.seat_group_ids = busSelected.seat_group_ids;
                updateForm(Object.assign(formeDetails, values));
                let fareAvailable = isFareAvailable(values, busSelected);
                if (fareAvailable) {
                    onMethodSelect(method, Object.assign(formeDetails, values));
                } else {
                    message.error("Fare unavailable. Enable default fare / add seat group ids for bus.");
                }
            }).catch((error) => {
            });
    }, [formeDetails, busSelected, onMethodSelect, form]);

    const layout = {
        labelCol: { span: 8 },
        wrapperCol: { span: 14 }
    };

    let isBusSelected = isValidArray(busSelected) && isValid(busSelected.id) ? true : false;

    const selectSeat = (seat) => {
        let index = selectedSeatNumbers.indexOf(seat.seat_number);
        if (index !== -1) {
            selectedSeatNumbers.splice(index, 1);
        } else {
            selectedSeatNumbers.push(seat.seat_number);
        }
        setSelectedSeatNumbers([...selectedSeatNumbers]);
    }

    return (
        <div className="create-trip-root">
            <div>
                <h3>
                    Select Bus and Method
                </h3>
            </div>
            <div className="create-trip-underline"></div>
            <Form form={form} name="basic" initialValues={{ trip_identifier: '' }} {...layout}>
                <Fragment>
                    <Form.Item label="Start Time:" name="start_time" rules={[{ required: true, message: "Please input Start Time!" }]}>
                        <TimePicker
                            className='remove-clear-icon'
                            showSearch
                            popupClassName={"time-picker-to-not-display-footer"}
                            onChange={updateStartTime}
                            onSelect={updateStartTime}
                            defaultOpenValue={moment('00:00', timeFormat)}
                            minuteStep={5} format={'hh:mm a'}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Select bus"
                        name="bus_id"
                        rules={[{ required: true, message: 'Please input select bus!' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select bus"
                            onChange={(value, componentData) => {
                                setBusSelected(componentData.props.data);
                            }}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={busesOptions}
                        />
                    </Form.Item>
                    {isBusSelected &&
                        <EditBlockedSeats
                            busId={busSelected.id}
                            blockedSeats={''}
                            selectSeat={selectSeat}
                            selectedSeatNumbers={selectedSeatNumbers}
                            justShowSeatLayout={true}
                        />
                    }
                    <Form.Item label="Trip Identifier: " name="trip_identifier"
                        rules={[{ required: false, message: "Please input your trip identifier!" }]}>
                        <Input />
                    </Form.Item>
                    {isValidArray(partnerDetails) && partnerDetails.length > 0 &&
                        <Form.Item label="Partner Code: " name="partner_code"
                            rules={[{ required: false, message: "Please input your partner code!" }]}>
                            <Select
                                showSearch
                                placeholder="Select partner"
                                onChange={(value, componentData) => {
                                    setSelectedPartner(componentData.props.data);
                                }}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    || option.props.key.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {partnerDetails.map(printPartnerOption)}
                            </Select>
                        </Form.Item>
                    }
                    <div>
                        <h3>Default Fare</h3>
                    </div>
                    {renderPossibleFares(busSelected)}
                    {isBusSelected &&
                        <EditBlockedSeats
                            busId={busSelected.id}
                            blockedSeats={''}
                            selectSeat={selectSeat}
                            selectedSeatNumbers={selectedSeatNumbers}
                        />
                    }
                    <Tooltip title="Enables the same seat to be booked by a second passenger after the first passenger gets down.">
                        <Form.Item
                            label="Seat Sharing"
                            name="cut_route_enabled"
                            valuePropName="checked"
                        >
                            <Switch onChange={(checked) => { setSeatSharing(checked) }} />
                        </Form.Item>
                    </Tooltip>
                    {isSeatSharing &&
                        <Tooltip title="A second passenger's boarding point can only be after the first passenger's dropping point.">
                            <Form.Item
                                label="Avoid Standing Passengers"
                                name="bp_check_enabled"
                                valuePropName="checked"
                            >
                                <Checkbox />
                            </Form.Item>
                        </Tooltip>
                    }
                </Fragment>
                <Divider className="trip-divider"/>
                <Row justify="space-around">
                    <Button
                        type="primary"
                        onClick={() => handleMethodSelect("time")}
                    >
                        Continue Time Method
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => handleMethodSelect("map")}
                    >
                        Continue Map Method
                    </Button>
                </Row>
            </Form>
        </div>
    )
} 